import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { OrderPreviewService, OrderPreviewType } from '../services/order-preview.service';

/** Navigation state that skips the preview and opens the full order page. */
export const OPEN_FULL_ORDER_STATE = { openFullOrder: true };

/**
 * Guards /orders/purchase-orders/:id and /orders/sales-orders/:id.
 *
 * When the user clicks a PO / SO link (routerLink, router.navigate, notifications, ...) while
 * being outside the Orders module, the order opens in the preview modal and the navigation is
 * cancelled, so the current page keeps its state. The full page still opens when:
 * - the user is already inside the Orders module,
 * - the URL is loaded directly, refreshed or opened in a new tab (first navigation),
 * - the browser back / forward buttons are used,
 * - the navigation asks for it (OPEN_FULL_ORDER_STATE, e.g. "Open full order" in the modal).
 */
export function orderPreviewGuard(type: OrderPreviewType): CanActivateFn {
  return (route) => {
    const router = inject(Router);
    const navigation = router.currentNavigation();

    if (!router.navigated) return true;
    if (navigation?.trigger !== 'imperative') return true;
    if (navigation.extras.state?.['openFullOrder']) return true;
    if (router.url.startsWith('/orders')) return true;

    const id = route.paramMap.get('id');
    if (!id) return true;

    inject(OrderPreviewService).open(type, id);
    return false;
  };
}
